// ============================================================================
// BP-13-02: Admin Moderation Hooks
// BrightPath Platform - Admin Frontend
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminModerationApi } from '@/features/admin/api/adminApi';
import type {
  ModerationQueueParams,
  ModerationActionRequest,
} from '@/types/admin';
import { useToast } from '@/core/hooks/useToast';

// =============================================================================
// Query Keys
// =============================================================================

export const moderationKeys = {
  all: ['admin', 'moderation'] as const,
  queue: () => [...moderationKeys.all, 'queue'] as const,
  queueList: (params: ModerationQueueParams) => [...moderationKeys.queue(), params] as const,
  items: () => [...moderationKeys.all, 'items'] as const,
  item: (id: string) => [...moderationKeys.items(), id] as const,
  history: (id: string) => [...moderationKeys.item(id), 'history'] as const,
  stats: () => [...moderationKeys.all, 'stats'] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch moderation queue
 */
export function useModerationQueue(params: ModerationQueueParams) {
  return useQuery({
    queryKey: moderationKeys.queueList(params),
    queryFn: () => adminModerationApi.getQueue(params),
    staleTime: 15 * 1000, // 15 seconds - queue updates frequently
    refetchInterval: 30 * 1000, // Auto-refresh every 30 seconds
  });
}

/**
 * Fetch a single moderation item
 */
export function useModerationItem(itemId: string) {
  return useQuery({
    queryKey: moderationKeys.item(itemId),
    queryFn: () => adminModerationApi.getItem(itemId),
    enabled: !!itemId,
  });
}

/**
 * Fetch moderation history for an item
 */
export function useModerationHistory(itemId: string) {
  return useQuery({
    queryKey: moderationKeys.history(itemId),
    queryFn: () => adminModerationApi.getHistory(itemId),
    enabled: !!itemId,
  });
}

/**
 * Fetch moderation statistics
 */
export function useModerationStats() {
  return useQuery({
    queryKey: moderationKeys.stats(),
    queryFn: () => adminModerationApi.getStats(),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Auto-refresh every minute
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Take action on a moderation item
 */
export function useModerationAction() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ itemId, action }: { itemId: string; action: ModerationActionRequest }) =>
      adminModerationApi.takeAction(itemId, action),
    onSuccess: (item, { action }) => {
      queryClient.invalidateQueries({ queryKey: moderationKeys.queue() });
      queryClient.setQueryData(moderationKeys.item(item.id), item);
      queryClient.invalidateQueries({ queryKey: moderationKeys.stats() });
      
      const actionMessages: Record<string, string> = {
        approve: 'Content has been approved.',
        reject: 'Content has been rejected.',
        flag: 'Content has been flagged for further review.',
        escalate: 'Content has been escalated to senior moderator.',
        delete: 'Content has been deleted.',
        warn_user: 'User has been warned.',
      };

      toast({
        title: 'Action completed',
        description: actionMessages[action.action] || 'Action completed successfully.',
        variant: action.action === 'approve' ? 'success' : 'info',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Action failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Bulk approve items
 */
export function useBulkApprove() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (itemIds: string[]) => adminModerationApi.bulkApprove(itemIds),
    onSuccess: ({ processed }) => {
      queryClient.invalidateQueries({ queryKey: moderationKeys.queue() });
      queryClient.invalidateQueries({ queryKey: moderationKeys.stats() });
      toast({
        title: 'Bulk approve complete',
        description: `${processed} items have been approved.`,
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Bulk approve failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Bulk reject items
 */
export function useBulkReject() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ itemIds, reason }: { itemIds: string[]; reason?: string }) =>
      adminModerationApi.bulkReject(itemIds, reason),
    onSuccess: ({ processed }) => {
      queryClient.invalidateQueries({ queryKey: moderationKeys.queue() });
      queryClient.invalidateQueries({ queryKey: moderationKeys.stats() });
      toast({
        title: 'Bulk reject complete',
        description: `${processed} items have been rejected.`,
        variant: 'info',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Bulk reject failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

// =============================================================================
// Approve/Reject Shortcut Hooks
// =============================================================================

/**
 * Quick approve a moderation item
 */
export function useApproveItem() {
  const moderationAction = useModerationAction();

  return {
    ...moderationAction,
    mutate: (itemId: string) =>
      moderationAction.mutate({
        itemId,
        action: { action: 'approve', notifyUser: false },
      }),
    mutateAsync: (itemId: string) =>
      moderationAction.mutateAsync({
        itemId,
        action: { action: 'approve', notifyUser: false },
      }),
  };
}

/**
 * Quick reject a moderation item
 */
export function useRejectItem() {
  const moderationAction = useModerationAction();

  return {
    ...moderationAction,
    mutate: (itemId: string, notes?: string) =>
      moderationAction.mutate({
        itemId,
        action: { action: 'reject', notes, notifyUser: true },
      }),
    mutateAsync: (itemId: string, notes?: string) =>
      moderationAction.mutateAsync({
        itemId,
        action: { action: 'reject', notes, notifyUser: true },
      }),
  };
}

/**
 * Escalate a moderation item
 */
export function useEscalateItem() {
  const moderationAction = useModerationAction();

  return {
    ...moderationAction,
    mutate: (itemId: string, notes?: string) =>
      moderationAction.mutate({
        itemId,
        action: { action: 'escalate', notes, notifyUser: false },
      }),
    mutateAsync: (itemId: string, notes?: string) =>
      moderationAction.mutateAsync({
        itemId,
        action: { action: 'escalate', notes, notifyUser: false },
      }),
  };
}


